
import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../../../hooks/useAuth';
import { groupsApi, activitiesApi } from '../../../../lib/api';
import { useReputationRewards } from '../../../../hooks/useReputationRewards';
import { supabase, validateSession } from '../../../../lib/supabase';
import EmojiPicker from './EmojiPicker';

interface CreateMomentModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  groupId: string;
  groupName: string;
  onMomentCreated: () => void;
  inline?: boolean;
}

interface UploadingImage {
  id: string;
  file: File;
  preview: string;
  progress: 'uploading' | 'done' | 'error';
  url?: string;
  errorMsg?: string;
}

const MAX_IMAGES = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

export default function CreateMomentModal({
  isOpen,
  onClose,
  groupId,
  groupName,
  onMomentCreated,
  inline = false,
}: CreateMomentModalProps) {
  const { user, profile, isAuthenticated } = useAuth();
  const { awardPoints } = useReputationRewards();
  const [content, setContent] = useState('');
  const [images, setImages] = useState<UploadingImage[]>([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        200
      )}px`;
    }
  }, [content]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      images.forEach((img) => {
        if (img.preview.startsWith('blob:')) {
          URL.revokeObjectURL(img.preview);
        }
      });
    };
  }, []);

  const handleEmojiSelect = (emoji: string) => {
    if (textareaRef.current) {
      const start = textareaRef.current.selectionStart;
      const end = textareaRef.current.selectionEnd;
      const newContent =
        content.substring(0, start) + emoji + content.substring(end);
      setContent(newContent);
      setTimeout(() => {
        if (textareaRef.current) {
          const newPos = start + emoji.length;
          textareaRef.current.selectionStart = newPos;
          textareaRef.current.selectionEnd = newPos;
          textareaRef.current.focus();
        }
      }, 0);
    } else {
      setContent((prev) => prev + emoji);
    }
  };

  const uploadSingleImage = async (file: File): Promise<string> => {
    if (!user) throw new Error('Please sign in to upload images');

    const validSession = await validateSession();
    if (!validSession)
      throw new Error('Session expired. Please sign in again.');

    const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const fileName = `${Date.now()}-${Math.random()
      .toString(36)
      .substring(2, 10)}.${fileExt}`;
    const filePath = `post-images/${user.id}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('images')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
        metadata: { userId: user.id, uploadedAt: new Date().toISOString() },
      });

    if (uploadError) {
      if (uploadError.message.includes('duplicate')) {
        const retryName = `${Date.now()}-${Math.random()
          .toString(36)
          .substring(2, 10)}-r.${fileExt}`;
        const retryPath = `post-images/${user.id}/${retryName}`;
        const { error: retryErr } = await supabase.storage
          .from('images')
          .upload(retryPath, file, {
            cacheControl: '3600',
            upsert: false,
          });
        if (retryErr) throw new Error(`Upload failed: ${retryErr.message}`);
        const { data: retryUrl } = supabase.storage
          .from('images')
          .getPublicUrl(retryPath);
        return retryUrl.publicUrl;
      }
      throw new Error(`Upload failed: ${uploadError.message}`);
    }

    const { data: urlData } = supabase.storage
      .from('images')
      .getPublicUrl(filePath);
    if (!urlData.publicUrl) throw new Error('Failed to get image URL');
    return urlData.publicUrl;
  };

  const handleFilesSelected = useCallback(
    async (files: FileList | File[]) => {
      if (!user || !isAuthenticated) {
        setError('Please sign in to upload images');
        return;
      }

      const fileArray = Array.from(files);
      const currentCount = images.filter((i) => i.progress !== 'error')
        .length;
      const remaining = MAX_IMAGES - currentCount;

      if (remaining <= 0) {
        setError(`Maximum ${MAX_IMAGES} images allowed`);
        return;
      }

      const toUpload = fileArray.slice(0, remaining);
      const invalid = toUpload.filter((f) => !ALLOWED_TYPES.includes(f.type));
      if (invalid.length > 0) {
        setError('Only JPEG, PNG, GIF, and WebP images are allowed');
        return;
      }
      const oversized = toUpload.filter((f) => f.size > MAX_FILE_SIZE);
      if (oversized.length > 0) {
        setError('Each image must be less than 5MB');
        return;
      }

      setError('');

      // Create placeholder entries with local previews
      const newEntries: UploadingImage[] = toUpload.map((file) => ({
        id: `${Date.now()}-${Math.random()
          .toString(36)
          .substring(2, 8)}`,
        file,
        preview: URL.createObjectURL(file),
        progress: 'uploading' as const,
      }));

      setImages((prev) => [...prev, ...newEntries]);

      // Upload each in parallel (sequentially to keep ordering)
      for (const entry of newEntries) {
        try {
          const url = await uploadSingleImage(entry.file);
          setImages((prev) =>
            prev.map((img) =>
              img.id === entry.id ? { ...img, progress: 'done' as const, url } : img
            )
          );
        } catch (err: any) {
          console.error('Upload error:', err);
          setImages((prev) =>
            prev.map((img) =>
              img.id === entry.id
                ? {
                    ...img,
                    progress: 'error' as const,
                    errorMsg: err.message || 'Upload failed',
                  }
                : img
            )
          );
        }
      }
    },
    [user, isAuthenticated, images]
  );

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFilesSelected(e.target.files);
      e.target.value = '';
    }
  };

  const removeImage = (id: string) => {
    setImages((prev) => {
      const img = prev.find((i) => i.id === id);
      if (img && img.preview.startsWith('blob:')) {
        URL.revokeObjectURL(img.preview);
      }
      return prev.filter((i) => i.id !== id);
    });
  };

  const retryUpload = async (id: string) => {
    const entry = images.find((i) => i.id === id);
    if (!entry) return;

    setImages((prev) =>
      prev.map((img) =>
        img.id === id ? { ...img, progress: 'uploading' as const, errorMsg: undefined } : img
      )
    );

    try {
      const url = await uploadSingleImage(entry.file);
      setImages((prev) =>
        prev.map((img) =>
          img.id === id ? { ...img, progress: 'done' as const, url } : img
        )
      );
    } catch (err: any) {
      setImages((prev) =>
        prev.map((img) =>
          img.id === id
            ? {
                ...img,
                progress: 'error' as const,
                errorMsg: err.message || 'Upload failed',
              }
            : img
        )
      );
    }
  };

  // Handle paste images
  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const imageFiles: File[] = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          const file = items[i].getAsFile();
          if (file) imageFiles.push(file);
        }
      }
      if (imageFiles.length > 0) {
        e.preventDefault();
        handleFilesSelected(imageFiles);
      }
    },
    [handleFilesSelected]
  );

  // Handle drag & drop
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current++;
    if (e.dataTransfer.types.includes('Files')) setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFilesSelected(e.dataTransfer.files);
    }
  };

  const handleSubmit = async () => {
    if (!user || !content.trim()) return;
    if (content.length > 5000) {
      setError('Content must be 5000 characters or less');
      return;
    }

    // Check if any images are still uploading
    const stillUploading = images.some((i) => i.progress === 'uploading');
    if (stillUploading) {
      setError('Please wait for images to finish uploading');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Collect successfully uploaded image URLs
      const uploadedUrls = images
        .filter((i) => i.progress === 'done' && i.url)
        .map((i) => i.url!);

      // Generate a default title from content (first 50 chars) or use "Group Moment"
      const defaultTitle = content.trim().substring(0, 50) || 'Group Moment';

      const postData = {
        author_id: user.id,
        group_id: groupId,
        title: defaultTitle,
        content,
        category_id: null,
        tags: null,
        images: uploadedUrls.length > 0 ? uploadedUrls : null,
        is_pinned: false,
        likes_count: 0,
        comments_count: 0,
        status: 'approved',
      };

      const response = await groupsApi.createGroupPost(groupId, postData);
      if (!response.success)
        throw new Error(response.error || 'Failed to create moment');

      const rewardResult = await awardPoints(user.id, 'create_post');
      const pointsEarned = rewardResult.success
        ? rewardResult.pointsAwarded
        : 0;

      activitiesApi
        .createActivity({
          user_id: user.id,
          type: 'group_post',
          title: 'Shared a moment in group',
          description: content.trim().substring(0, 100),
          data: {
            post_id: response.data?.id,
            group_id: groupId,
            group_name: groupName,
          },
        })
        .catch((err) =>
          console.warn('Activity creation failed:', err)
        );

      // Cleanup previews
      images.forEach((img) => {
        if (img.preview.startsWith('blob:')) URL.revokeObjectURL(img.preview);
      });

      setContent('');
      setImages([]);
      setIsFocused(false);
      onMomentCreated();
      if (onClose) onClose();

      const notification = document.createElement('div');
      notification.className =
        'fixed top-4 right-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-5 py-3 rounded-xl shadow-lg z-50 transition-all duration-300 flex items-center gap-2 text-sm';
      notification.innerHTML = `<i class="ri-check-circle-fill text-lg"></i><span>Moment shared!${
        pointsEarned > 0 ? ` +${pointsEarned} pts` : ''
      }</span>`;
      document.body.appendChild(notification);
      setTimeout(() => {
        notification.classList.add('opacity-0', 'translate-y-[-10px]');
        setTimeout(() => notification.remove(), 300);
      }, 2500);
    } catch (err: any) {
      console.error('Error creating moment:', err);
      setError(err.message ?? 'Failed to share moment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated || !user) return null;

  const isExpanded =
    isFocused || content.trim().length > 0 || images.length > 0;
  const avatarUrl = profile?.avatar_url;
  const avatarInitial = (
    (profile?.username || user.email || 'U').charAt(0) ?? 'U'
  ).toUpperCase();
  const doneCount = images.filter((i) => i.progress === 'done').length;
  const uploadingCount = images.filter((i) => i.progress === 'uploading')
    .length;

  return (
    <div
      className={`bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-colors ${
        isDragging ? 'border-teal-400 bg-teal-50/30' : ''
      }`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        multiple
        className="hidden"
        onChange={handleFileInputChange}
      />

      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 bg-teal-50/80 border-2 border-dashed border-teal-400 rounded-xl z-10 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <i className="ri-image-add-line text-3xl text-teal-500 mb-1"></i>
            <p className="text-sm font-medium text-teal-700">
              Drop images here
            </p>
          </div>
        </div>
      )}

      {/* Composer header */}
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={profile?.username || 'You'}
                className="w-10 h-10 rounded-full object-cover ring-2 ring-gray-100"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center text-white font-semibold text-sm ring-2 ring-gray-100">
                {avatarInitial}
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onPaste={handlePaste}
              placeholder={`What's on your mind, ${
                profile?.username || 'friend'
              }?`}
              maxLength={5000}
              rows={1}
              className={`w-full resize-none border-0 outline-none text-sm text-gray-800 placeholder-gray-400 bg-gray-50 rounded-2xl px-4 py-2.5 transition-all duration-200 ${
                isExpanded
                  ? 'min-h-[80px] bg-gray-50/80'
                  : 'min-h-[40px]'
              }`}
            />

            {/* Minimized image thumbnails under the textarea */}
            {images.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {images.map((img) => (
                  <div key={img.id} className="relative group">
                    <div className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 flex-shrink-0">
                      <img
                        src={img.preview}
                        alt=""
                        className={`w-full h-full object-cover transition-opacity ${
                          img.progress === 'uploading'
                            ? 'opacity-50'
                            : img.progress === 'error'
                            ? 'opacity-40 grayscale'
                            : ''
                        }`}
                      />
                      {/* Uploading spinner overlay */}
                      {img.progress === 'uploading' && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-lg">
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                      {/* Error overlay */}
                      {img.progress === 'error' && (
                        <div className="absolute inset-0 flex items-center justify-center bg-red-900/30 rounded-lg">
                          <button
                            onClick={() => retryUpload(img.id)}
                            className="w-6 h-6 flex items-center justify-center bg-white rounded-full shadow-sm cursor-pointer"
                            title="Retry upload"
                          >
                            <i className="ri-refresh-line text-red-500 text-xs"></i>
                          </button>
                        </div>
                      )}
                      {/* Done checkmark */}
                      {img.progress === 'done' && (
                        <div className="absolute bottom-0.5 right-0.5 w-4 h-4 flex items-center justify-center bg-emerald-500 rounded-full">
                          <i className="ri-check-line text-white text-[9px]"></i>
                        </div>
                      )}
                    </div>
                    {/* Remove button */}
                    <button
                      onClick={() => removeImage(img.id)}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center bg-gray-800 hover:bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-all cursor-pointer shadow-sm"
                    >
                      <i className="ri-close-line text-[10px]"></i>
                    </button>
                  </div>
                ))}

                {/* Add more button */}
                {images.filter((i) => i.progress !== 'error')
                  .length < MAX_IMAGES && (
                  <button
                    onClick={handlePhotoClick}
                    className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 hover:border-teal-400 flex items-center justify-center text-gray-400 hover:text-teal-500 transition-colors cursor-pointer bg-gray-50 hover:bg-teal-50/50"
                  >
                    <i className="ri-add-line text-lg"></i>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Upload status */}
        {uploadingCount > 0 && (
          <div className="mt-2 ml-[52px] flex items-center gap-1.5 text-xs text-teal-600">
            <i className="ri-loader-4-line animate-spin text-sm"></i>
            <span>
              Uploading {uploadingCount} image
              {uploadingCount > 1 ? 's' : ''}...
            </span>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mt-3 ml-[52px] bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-600 flex items-center gap-1.5">
            <i className="ri-error-warning-line"></i>
            {error}
          </div>
        )}
      </div>

      {/* Action bar */}
      <div className="px-4 pb-3">
        <div className="ml-[52px] flex items-center justify-between border-t border-gray-100 pt-3">
          <div className="flex items-center gap-1">
            {/* Photo button */}
            <button
              type="button"
              onClick={handlePhotoClick}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors cursor-pointer"
            >
              <i className="ri-image-line text-base text-emerald-500"></i>
              <span>Photo</span>
              {images.length > 0 && (
                <span className="text-[10px] text-gray-400">
                  ({doneCount}/{MAX_IMAGES})
                </span>
              )}
            </button>

            {/* Emoji picker button */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer ${
                  showEmojiPicker
                    ? 'bg-amber-50 text-amber-600'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                }`}
              >
                <i className="ri-emotion-happy-line text-base text-amber-500"></i>
                <span>Emoji</span>
              </button>
              {showEmojiPicker && (
                <EmojiPicker
                  onSelect={handleEmojiSelect}
                  onClose={() => setShowEmojiPicker(false)}
                />
              )}
            </div>

            {/* Location button */}
            <button
              type="button"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors cursor-pointer"
              onClick={() => {
                setContent(
                  (prev) => prev + (prev ? '\n' : '') + '\uD83D\uDCCD '
                );
                textareaRef.current?.focus();
              }}
            >
              <i className="ri-map-pin-line text-base text-rose-500"></i>
              <span>Location</span>
            </button>
          </div>

          {/* Post button */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading || !content.trim() || uploadingCount > 0}
            className={`px-5 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap ${
              content.trim() && uploadingCount === 0
                ? 'bg-teal-500 hover:bg-teal-600 text-white shadow-sm cursor-pointer'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {loading ? (
              <span className="flex items-center gap-1.5">
                <i className="ri-loader-4-line animate-spin"></i>
                Posting...
              </span>
            ) : (
              'Post'
            )}
          </button>
        </div>

        {/* Character counter */}
        {isExpanded && content.length > 0 && (
          <div className="ml-[52px] mt-2">
            <span
              className={`text-[11px] ${
                content.length > 4500 ? 'text-amber-500' : 'text-gray-400'
              }`}
            >
              {content.length}/5000
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
